import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import VideoIcon from '@atlaskit/icon/core/video';
import SkeletonItem from '@atlaskit/menu/skeleton-item';
import { useIsReducedMotion } from '@atlaskit/motion/accessibility';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';

import { getHashedLoomVideoIdFromUrl } from '@atlassian/analytics-data-privacy';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant.ts';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';

import { buildCreationLink } from '../../CreateContentRouteComponent/index.ts';

import { LoadingPopoverItem } from './LoadingPopoverItem.tsx';
import {
	ContentIconWrapper,
	ContentTitle,
	TitleContainer,
	MenuItemWrapper,
} from './popoverItemStyles.tsx';

const i18n = defineMessages({
	openingLoomRecorder: {
		id: 'create-blank-fabric-page.create.content.loom-video.opening-loom-recorder',
		defaultMessage: 'Opening Loom recorder',
		description:
			'text announced to screen readers while waiting for the Loom recording UI to open after clicking the Loom video button in a create menu',
	},
	loomVideoTitle: {
		id: 'create-blank-fabric-page.create.content.loom-video.title',
		defaultMessage: 'Loom video',
		description:
			'title of the global or contextual create menu item indicating that this option will trigger a Loom video recording flow',
	},
});

const iconWrapperStyles = xcss({
	display: 'flex',
	flexShrink: 0,
});

export const useIsCreateMenuLoomRecorderEntryPointEnabled = (): boolean => {
	const { entryPointVariant } = useLoomEntryPointVariant();
	return ['BETA', 'CO_USE', 'CROSS_JOIN'].includes(entryPointVariant);
};

type RecordLoomPopoverItemProps = {
	entryPointLocation: string;
	onClick: (actionSubjectId: string) => void;
	onRecorderOpenComplete?: () => void;
	source: string;
	spaceKey?: string;
	parentId?: string;
	withFallback?: boolean;
	isDisabled?: boolean;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	location?: string;
};

const RecordLoomPopoverItemComponent = ({
	entryPointLocation,
	flags,
	onClick,
	onRecorderOpenComplete,
	source,
	spaceKey,
	parentId,
	withFallback = false,
	isDisabled = false,
	location = '',
}: RecordLoomPopoverItemProps & WithFlagsProps) => {
	const intl = useIntl();
	const prefersReducedMotion = useIsReducedMotion();
	const { push } = useRouteActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceId = useSpaceId();

	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'record-loom-popover-item',
	});

	const prevIsOpeningRecorder = useRef(false);

	const { openLoomRecorder, isLoomRecorderInitialized, isOpeningRecorder } =
		useLoomRecorderEntryPoint({ entryPointLocation, flags });

	useEffect(() => {
		if (prevIsOpeningRecorder.current && !isOpeningRecorder) {
			onRecorderOpenComplete?.();
		}
		prevIsOpeningRecorder.current = isOpeningRecorder;
	}, [isOpeningRecorder, onRecorderOpenComplete]);

	const handleClick = useCallback(() => {
		onClick('createLoomPopoverItem');

		void openLoomRecorder({
			onInsert: (videoUrl: string) => {
				const createUrl = buildCreationLink({
					contentType: 'smartLinkEmbed',
					parentPageId: parentId,
					...(withFallback && { withFallback }),
					smartLinkCreateSource: 'loomRecordSDK',
					smartLinkEmbedUrl: videoUrl,
					source,
					spaceKey,
				});

				push(createUrl);

				void getHashedLoomVideoIdFromUrl(videoUrl).then((hashedVideoId) => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'insertVideo',
							actionSubject: 'loom',
							source, // {globalCreateDropdown|pageTreeItemContextualCreate|spaceLevelContextualCreate}-smartLinkEmbed
							objectType: 'embed',
							containerType: 'space',
							containerId: spaceId, // spaceKey can have UGC so use spaceId
							attributes: {
								location: 'contentTreeItem',
								video_id: hashedVideoId,
							},
						},
					}).fire();
				});
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert video',
			location,
		});
	}, [
		onClick,
		openLoomRecorder,
		parentId,
		push,
		source,
		spaceKey,
		withFallback,
		createAnalyticsEvent,
		spaceId,
		location,
	]);

	const handleClickWithStorageEnforcement = enforceStorageLimit(handleClick);

	const menuItem = !isLoomRecorderInitialized ? (
		<MenuItemWrapper isDisabled={isDisabled}>
			<SkeletonItem
				hasIcon
				isShimmering={!prefersReducedMotion}
				testId="skeleton-create-menu-button-item"
			/>
		</MenuItemWrapper>
	) : (
		<MenuItemWrapper isDisabled={isDisabled}>
			<LoadingPopoverItem
				iconBefore={
					<ContentIconWrapper>
						<Box xcss={iconWrapperStyles}>
							<VideoIcon label="" color={token('color.icon.subtle')} />
						</Box>
					</ContentIconWrapper>
				}
				isLoading={isOpeningRecorder}
				loadingLabel={intl.formatMessage(i18n.openingLoomRecorder)}
				onClick={handleClickWithStorageEnforcement}
				testId="create-menu-button-item"
				isDisabled={isDisabled}
			>
				<TitleContainer>
					<ContentTitle>
						<FormattedMessage {...i18n.loomVideoTitle} />
					</ContentTitle>
				</TitleContainer>
			</LoadingPopoverItem>
		</MenuItemWrapper>
	);

	return menuItem;
};

export const RecordLoomPopoverItem: React.ComponentType<Omit<RecordLoomPopoverItemProps, 'flags'>> =
	withFlags(RecordLoomPopoverItemComponent);
